import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { WorkspaceSwitcher } from '@/components/WorkspaceSwitcher'
import { api } from '@/lib/api'
import { ROLE_TITLES } from '@/lib/types'
import { useActiveWorkspace, useAuthStore } from '@/stores/auth'

/**
 * Каркас приложения: боковая навигация, переключатель пространства, профиль.
 */

interface NavItem {
  to: string
  label: string
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Обзор' },
  { to: '/sources', label: 'Источники данных' },
  { to: '/dashboards', label: 'Дашборды' },
  { to: '/members', label: 'Участники' },
  { to: '/settings', label: 'Настройки' },
]

export function AppLayout() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const clearSession = useAuthStore((state) => state.clearSession)
  const workspace = useActiveWorkspace()

  async function handleLogout(): Promise<void> {
    try {
      await api.post('/auth/logout', undefined, { skipWorkspace: true })
    } catch {
      // Даже если сервер недоступен, локальную сессию нужно завершить:
      // пользователь нажал «Выйти» и ожидает, что вышел
    } finally {
      clearSession()
      void navigate('/login', { replace: true })
    }
  }

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <aside className="flex shrink-0 flex-col border-b border-slate-200 bg-white lg:w-64 lg:border-b-0 lg:border-r">
        <div className="flex items-center gap-2 px-5 py-4">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-sm font-bold text-white">
            RF
          </span>
          <span className="text-base font-semibold text-slate-900">ReportFlow</span>
        </div>

        <div className="border-y border-slate-100 px-4 py-3">
          <WorkspaceSwitcher />
        </div>

        <nav className="flex gap-1 overflow-x-auto px-3 py-3 lg:flex-1 lg:flex-col lg:overflow-visible">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `shrink-0 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden border-t border-slate-100 px-4 py-3 lg:block">
          <p className="truncate text-sm font-medium text-slate-800">
            {user?.full_name || user?.email}
          </p>
          <p className="truncate text-xs text-slate-500">{user?.email}</p>
          <button
            type="button"
            onClick={handleLogout}
            className="btn-ghost mt-2 w-full !justify-start px-2"
          >
            Выйти
          </button>
        </div>
      </aside>

      <main className="flex-1 bg-slate-50">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-3 lg:px-8">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold text-slate-900">
              {workspace?.name ?? 'Рабочее пространство'}
            </h1>
            {workspace && (
              <p className="truncate text-xs text-slate-500">
                {workspace.slug} · ваша роль: {ROLE_TITLES[workspace.role].toLowerCase()}
              </p>
            )}
          </div>
          <button type="button" onClick={handleLogout} className="btn-secondary lg:hidden">
            Выйти
          </button>
        </header>

        <div className="mx-auto max-w-6xl px-5 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
